export type LiveMode = "connecting" | "websocket" | "sse" | "polling" | "offline";

export interface LiveEvent { readonly eventId: string; readonly sequence: number; readonly eventType: string; readonly data: unknown; }

export class LiveConnection {
  #stopped = false; #socket?: WebSocket; #stream?: EventSource; #timer?: number; #cursor = 0;
  public constructor(readonly options: { tenantId: string; token: string; csrf: string; onMode: (mode: LiveMode) => void; onEvent: (event: LiveEvent) => void }) {}

  public start(): void { this.#stopped = false; this.options.onMode("connecting"); void this.#connectWebSocket(); }
  public stop(): void { this.#stopped = true; this.#socket?.close(); this.#stream?.close(); if (this.#timer !== undefined) window.clearTimeout(this.#timer); }

  async #ticket(): Promise<string> {
    const response = await fetch("/api/v1/realtime/tickets", { method: "POST", headers: { "content-type": "application/json", ...(this.options.token===""?{}:{authorization:`Bearer ${this.options.token}`}), "x-csrf-token":this.options.csrf }, body: JSON.stringify({ tenant_id: this.options.tenantId }), cache: "no-store" });
    if (!response.ok) throw new Error("ticket failed");
    return (await response.json() as { ticket: string }).ticket;
  }

  async #connectWebSocket(): Promise<void> {
    if (this.#stopped) return;
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    try {
      const ticket = await this.#ticket();
      const url = `${protocol}//${location.host}/api/v1/realtime/ws?ticket=${encodeURIComponent(ticket)}&cursor=${this.#cursor}`;
      const socket = new WebSocket(url); this.#socket = socket;
      const fallback = window.setTimeout(() => { if (socket.readyState !== WebSocket.OPEN) { socket.close(); this.#connectSse(); } }, 3_000);
      socket.onopen = () => { window.clearTimeout(fallback); this.options.onMode("websocket"); };
      socket.onmessage = (message) => this.#accept(JSON.parse(String(message.data)) as LiveEvent);
      socket.onclose = () => { window.clearTimeout(fallback); if (!this.#stopped) this.#connectSse(); };
      socket.onerror = () => socket.close();
    } catch { this.#connectSse(); }
  }

  async #connectSse(): Promise<void> {
    if (this.#stopped) return;
    try {
      const ticket = await this.#ticket();
      const url = `/api/v1/realtime/sse?ticket=${encodeURIComponent(ticket)}&cursor=${this.#cursor}`;
      const stream = new EventSource(url); this.#stream = stream;
      const fallback = window.setTimeout(() => { if (stream.readyState !== EventSource.OPEN) { stream.close(); this.#poll(); } }, 3_000);
      stream.onopen = () => { window.clearTimeout(fallback); this.options.onMode("sse"); };
      stream.onmessage = (message) => this.#accept(JSON.parse(message.data) as LiveEvent);
      stream.onerror = () => { window.clearTimeout(fallback); stream.close(); if (!this.#stopped) this.#poll(); };
    } catch { void this.#poll(); }
  }

  async #poll(): Promise<void> {
    if (this.#stopped) return; this.options.onMode("polling");
    try {
      const response = await fetch(`/api/v1/realtime/events?tenant=${encodeURIComponent(this.options.tenantId)}&cursor=${this.#cursor}&limit=100`, { headers: this.options.token===""?{}:{authorization:`Bearer ${this.options.token}`}, cache: "no-store" });
      if (!response.ok) throw new Error("poll failed");
      const body = await response.json() as { events: LiveEvent[]; cursor: number };
      body.events.forEach((event) => this.#accept(event)); this.#cursor = Math.max(this.#cursor, body.cursor);
      this.#timer = window.setTimeout(() => void this.#poll(), 4_000);
    } catch { this.options.onMode("offline"); this.#timer = window.setTimeout(() => void this.#connectWebSocket(), 8_000); }
  }

  #accept(event: LiveEvent): void { if (event.sequence <= this.#cursor) return; this.#cursor = event.sequence; this.options.onEvent(event); }
}
